import React, { useEffect, useState, useRef, createRef } from "react";
import Head from "next/head";
import { Box } from "@chakra-ui/core";

import FoodMenu from "components/food/FoodMenu";
import FoodModal from "components/food/FoodModal";
import MenuBar from "components/home/MenuBar";
import Banner from "components/home/Banner";
import EatingPlaceModal from "components/home/EatingPlaceModal";

import LoadingPage from "components/shared/LoadingPage";
import { useCollection } from "@nandorojo/swr-firestore";
import getDimensions from "utils/shared/getDimensions";

// const Home = ({ foods, categories }) => {
const Home = () => {
  const [foods, setFoods] = useState([]);
  const [categories, setCategories] = useState([]);
  const [sectionsRef, setSectionsRef] = useState([]);
  const [sectionRefs, setSectionRefs] = useState([]);

  ////////////////////////////////////////////////////////////////

  const { data: categoriesData } = useCollection(
    "categories",
    { listen: true },
    {
      revalidateOnFocus: false,
    }
  );

  const { data: foodsData } = useCollection(
    "foods",
    { listen: true },
    {
      revalidateOnFocus: false,
    }
  );

  const [optionsData, setOptionsData] = useState();
  const [optionItemsData, setOptionItemsData] = useState();

  const { data: rawOptionsData } = useCollection(
    "options",
    { listen: true },
    {
      revalidateOnFocus: false,
    }
  );

  const { data: rawOptionItemsData } = useCollection(
    "optionItems",
    { listen: true },
    {
      revalidateOnFocus: false,
    }
  );

  useEffect(() => {
    setOptionsData(rawOptionsData);
    setOptionItemsData(rawOptionItemsData);
  }, [rawOptionsData, rawOptionItemsData]);

  const processFoodsData = () => {
    // Sorts the categories by position
    categoriesData.sort(function (a, b) {
      return a.position - b.position;
    });

    // Groups the food items by category id.
    let processedFoods = foodsData.reduce((res, food) => {
      if (!res[food.categoryId]) {
        res[food.categoryId] = [];
      }
      res[food.categoryId].push(food);
      return res;
    }, {});

    processedFoods = Object.entries(processedFoods); // So i don't have to use Object.entries everywhere

    // Replaces the category id with category name
    for (let i = 0; i < processedFoods.length; i++) {
      processedFoods[i][0] = categoriesData.find((c) => c.id === processedFoods[i][0]).name;

      for (let j = 0; j < processedFoods[i][1].length; j++) {
        processedFoods[i][1][j].categoryName = categoriesData.find(
          (c) => c.id === processedFoods[i][1][j].categoryId
        ).name;
      }
    }

    // Sorts by category order
    processedFoods = processedFoods.sort(function (a, b) {
      return categoriesData.findIndex((c) => c.name == a[0]) - categoriesData.findIndex((c) => c.name === b[0]);
    });

    setFoods(processedFoods);
    setCategories(categoriesData);
  };

  useEffect(() => {
    if (categoriesData && foodsData) {
      processFoodsData();
    }
  }, [foodsData, categoriesData]);

  ////////////////////////////////////////////////////////////////

  useEffect(() => {
    if (foods.length) {
      const createdRefs = foods.map(() => createRef());
      setSectionsRef(createdRefs);

      let groupedRefs = [];
      foods.map(([sectionName], i) => {
        groupedRefs.push({ section: sectionName, ref: createdRefs[i] });
      });
      setSectionRefs(groupedRefs);
    }
  }, [foods]);

  const [visibleSection, setVisibleSection] = useState();
  const menuBarRef = useRef(null);

  const isBrowser = () => typeof window !== "undefined";

  useEffect(() => {
    const handleScroll = () => {
      const { height: headerHeight } = getDimensions(menuBarRef.current);
      const scrollPosition = window.scrollY + headerHeight;

      const selected = sectionRefs.find(({ section, ref }) => {
        const ele = ref.current;
        if (ele) {
          const { offsetBottom, offsetTop } = getDimensions(ele);
          return scrollPosition > offsetTop && scrollPosition < offsetBottom;
        }
      });

      if (selected && selected.section !== visibleSection) {
        setVisibleSection(selected.section);
      } else if (!selected && visibleSection) {
        setVisibleSection(undefined);
      }
    };

    if (foods.length && categories.length) {
      handleScroll();
      window.addEventListener("scroll", handleScroll);
    }

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [visibleSection, isBrowser]);

  if (sectionsRef === undefined || !foods.length || !categories.length) return <LoadingPage />;

  if (sectionsRef !== undefined && foods.length && categories.length)
    return (
      <>
        <Head>
          <title>Commander | La Citadelle</title>
        </Head>
        <Banner />
        <EatingPlaceModal />
        <Box bg="gray.50">
          <MenuBar foods={foods} sectionsRef={sectionsRef} ref={menuBarRef} visibleSection={visibleSection} />

          <Box
            maxW="7xl"
            mx="auto"
            pb="24"
            px={{ base: "4", sm: "6", lg: "8" }}
            mt={visibleSection !== undefined ? "16" : "0"}
            overflow="hidden"
          >
            <FoodModal optionsData={optionsData} optionItemsData={optionItemsData} />
            <FoodMenu foods={foods} sectionsRef={sectionsRef} categories={categories} />
          </Box>
        </Box>
      </>
    );
};

export default Home;

/*
// This gets called on every request
export async function getServerSideProps() {
  // Listeners don't work, with admin.firestore because back-end
  let initialFoods = [];
  let categories = [];

  const catSnapshot = await db.collection("categories").get();
  catSnapshot.forEach((doc) => {
    categories.push({ id: doc.id, ...doc.data() });
  });

  // const snapshot = await db.collection("foods").where("visible", "==", true).get();
  const snapshot = await db.collection("foods").get();
  snapshot.forEach((doc) => {
    initialFoods.push({ id: doc.id, ...doc.data() });
  });

  // Sorts the categories by position
  categories.sort(function (a, b) {
    return a.position - b.position;
  });

  // Groups the food items by category id.
  let foods = initialFoods.reduce((res, food) => {
    if (!res[food.categoryId]) {
      res[food.categoryId] = [];
    }
    res[food.categoryId].push(food);
    return res;
  }, {});

  // So i don't have to use Object.entries everywhere
  foods = Object.entries(foods);

  // Replaces the category id with category name
  for (let i = 0; i < foods.length; i++) {
    foods[i][0] = categories.find((c) => c.id === foods[i][0]).name;

    for (let j = 0; j < foods[i][1].length; j++) {
      foods[i][1][j].categoryName = categories.find((c) => c.id === foods[i][1][j].categoryId).name;
    }
  }

  // Sorts by category order
  foods = foods.sort(function (a, b) {
    return categories.findIndex((c) => c.name == a[0]) - categories.findIndex((c) => c.name === b[0]);
  });

  return {
    props: {
      foods,
      categories,
    },
  };
}
*/
